import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import ActionList from '../components/actions/ActionList';
import actionService from '../services/actionService';
import targetService from '../services/targetService';
import projectService from '../services/projectService';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

function ActionsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const [actions, setActions] = useState([]);
  const [targets, setTargets] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [actionsData, targetsData, projectData] = await Promise.all([
        actionService.getActionsByProject(projectId),
        targetService.getTargetsByProject(projectId),
        projectService.getProject(projectId)
      ]);
      setActions(Array.isArray(actionsData) ? actionsData : []);
      setTargets(Array.isArray(targetsData) ? targetsData : []);
      setProject(projectData);
    } catch (error) {
      console.error('Error fetching actions data:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleActionDeleted = (deletedId) => {
    setActions(prev => prev.filter(a => a._id !== deletedId));
  };

  const handleBulkDelete = (deletedIds) => {
    const deletedSet = new Set(deletedIds);
    setActions(prev => prev.filter(a => !deletedSet.has(a._id)));
  };

  const handleGenerateCampaign = (campaign) => {
    navigate(`/projects/${projectId}/campaigns`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link
            to={`/projects/${projectId}`}
            className={`text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
            {t('common.backToProject') || 'Back to Project'}
          </Link>
          <h1 className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('actions.title') || 'Actions'}
          </h1>
          {project && (
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('common.project')}: {project.name}
            </p>
          )}
        </div>
        <Link
          to={`/projects/${projectId}/actions/new`}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          {t('actions.logAction') || 'Log Action'}
        </Link>
      </div>

      <ActionList
        actions={actions}
        projectId={projectId}
        onActionDeleted={handleActionDeleted}
        onBulkDelete={handleBulkDelete}
        onGenerateCampaign={handleGenerateCampaign}
        targets={targets}
      />
    </div>
  );
}

export default ActionsPage;
